import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSystem } from '@ohif/core';
import { usePolling } from './usePolling';
import { DiagnosisState } from '../utils';
import { DiagnosisService } from '../services/DiagnosisService';

export function useDiagnosisStatus(studyInstanceUid?: string): {
  state: DiagnosisState;
  trigger: () => void;
  revoke: () => void;
} {
  const { servicesManager } = useSystem();
  const { diagnosisService } = servicesManager.services;

  const [state, setState] = useState<DiagnosisState>(() =>
    studyInstanceUid ? diagnosisService.getState(studyInstanceUid) : DiagnosisState.IDLE
  );

  const refresh = useCallback(
    () => (studyInstanceUid ? diagnosisService.refresh(studyInstanceUid) : Promise.resolve()),
    [diagnosisService, studyInstanceUid]
  );

  const { startPolling, stopPolling } = usePolling(refresh);

  useEffect(() => {
    if (!studyInstanceUid) {
      return undefined;
    }

    const apply = () => {
      const next = diagnosisService.getState(studyInstanceUid);
      setState(next);

      if (next === DiagnosisState.PROCESSING) {
        startPolling(refresh);
      } else {
        stopPolling();
      }
    };

    const subscriptions = [
      diagnosisService.subscribe(
        DiagnosisService.EVENTS.DIAGNOSIS_STATE_CHANGED,
        ({ studyInstanceUid: changed }) => {
          if (changed === studyInstanceUid) {
            apply();
          }
        }
      ),

      diagnosisService.subscribe(
        DiagnosisService.EVENTS.SESSION_LOST,
        ({ studyInstanceUid: changed }) => {
          if (changed === studyInstanceUid) {
            stopPolling();
          }
        }
      ),
    ];

    apply();
    void diagnosisService.track(studyInstanceUid);

    return () => {
      subscriptions.forEach(subscription => subscription.unsubscribe());
      stopPolling();
    };
  }, [diagnosisService, studyInstanceUid, refresh, startPolling, stopPolling]);

  const trigger = useCallback(() => {
    if (studyInstanceUid) {
      void diagnosisService.trigger(studyInstanceUid);
    }
  }, [diagnosisService, studyInstanceUid]);

  const revoke = useCallback(() => {
    if (studyInstanceUid) {
      void diagnosisService.revoke(studyInstanceUid);
    }
  }, [diagnosisService, studyInstanceUid]);

  return useMemo(() => ({ state, trigger, revoke }), [state, trigger, revoke]);
}
